<template>
  <div class="container py-4">
    <h5 class="font-weight-bold mb-4">
      <i class="fas fa-truck text-primary mr-2"></i>Cấu hình nhà vận chuyển
    </h5>

    <b-overlay :show="isLoading" rounded="sm">
      <!-- ViettelPost -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <span class="font-weight-bold mr-3">ViettelPost</span>
            <b-badge :variant="vtpConfig && vtpConfig.isActive ? 'success' : 'secondary'">
              {{ vtpConfig && vtpConfig.isActive ? 'Đang hoạt động' : 'Tạm ngưng' }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center" style="gap: 8px">
            <b-button
              v-if="vtpConfig || hasVtpFormData"
              size="sm"
              variant="outline-info"
              @click="testVtpConnection"
              :disabled="testingCarrier === 'viettelpost'"
            >
              <b-spinner v-if="testingCarrier === 'viettelpost'" small class="mr-1" />
              <i v-else class="fas fa-plug mr-1"></i>
              Test kết nối
            </b-button>
            <b-form-checkbox
              v-if="vtpConfig"
              v-model="vtpConfig.isActive"
              switch
              @change="(v) => toggleActive('viettelpost', v, vtpConfig.id)"
            />
          </div>
        </div>

        <div class="card-body">
          <b-form @submit.prevent="saveVtp">
            <h6 class="text-muted mb-3">Thông tin tài khoản</h6>
            <b-form-group label="Phương thức xác thực">
              <b-form-radio-group
                v-model="vtpForm.authMethod"
                :options="[
                  { text: 'Secret Token (khuyến nghị)', value: 'secretToken' },
                  { text: 'Username / Password', value: 'credentials' },
                ]"
              />
            </b-form-group>
            <b-form-group v-if="vtpForm.authMethod === 'secretToken'" label="Secret Token">
              <b-form-input
                v-model="vtpForm.vtpSecretToken"
                type="password"
                :placeholder="vtpForm.hasCredentials ? '••••••• (đã cấu hình – để trống nếu không đổi)' : 'Lấy từ partner.viettelpost.vn → Cấu hình tài khoản'"
              />
              <b-form-text>Đăng nhập <a href="https://partner.viettelpost.vn" target="_blank">partner.viettelpost.vn</a> → Cấu hình → API token</b-form-text>
            </b-form-group>
            <template v-if="vtpForm.authMethod === 'credentials'">
              <b-row>
                <b-col cols="6">
                  <b-form-group label="Tên đăng nhập">
                    <b-form-input v-model="vtpForm.username" />
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group label="Mật khẩu">
                    <b-form-input
                      v-model="vtpForm.password"
                      type="password"
                      :placeholder="vtpForm.hasCredentials ? '••••••• (để trống nếu không đổi)' : ''"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </template>
            <div class="d-flex justify-content-end">
              <b-button type="submit" variant="primary" :disabled="saving === 'viettelpost'">
                <b-spinner v-if="saving === 'viettelpost'" small class="mr-1" />
                {{ saving === 'viettelpost' ? 'Đang lưu...' : 'Kết nối & Lưu' }}
              </b-button>
            </div>
          </b-form>

          <div v-if="vtpConfig && vtpConfig.vtpTokenExpiredAt" class="mt-3 p-2 bg-light rounded" style="font-size:12px">
            Token hết hạn: <strong>{{ formatDate(vtpConfig.vtpTokenExpiredAt) }}</strong>
          </div>
        </div>
      </div>

      <!-- Nhất Tín -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <span class="font-weight-bold mr-3">Nhất Tín Logistics</span>
            <b-badge :variant="ntConfig && ntConfig.isActive ? 'success' : 'secondary'">
              {{ ntConfig && ntConfig.isActive ? 'Đang hoạt động' : 'Tạm ngưng' }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center" style="gap: 8px">
            <b-button
              v-if="ntSelectedCompanyId && (ntConfig || hasNtFormData)"
              size="sm"
              variant="outline-info"
              @click="testNtConnection"
              :disabled="testingCarrier === 'nhattin'"
            >
              <b-spinner v-if="testingCarrier === 'nhattin'" small class="mr-1" />
              <i v-else class="fas fa-plug mr-1"></i>
              Test kết nối
            </b-button>
            <b-form-checkbox
              v-if="ntConfig"
              v-model="ntConfig.isActive"
              switch
              @change="(v) => toggleActive('nhattin', v, ntConfig.id)"
            />
          </div>
        </div>

        <div class="card-body">
          <!-- Chọn doanh nghiệp -->
          <b-form-group label="Doanh nghiệp" class="mb-3">
            <b-col cols="3" class="px-0">
              <b-form-select
                v-model="ntSelectedCompanyId"
                :options="companyOptions"
                @change="onNtCompanyChange"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>-- Chọn doanh nghiệp --</b-form-select-option>
                </template>
              </b-form-select>
            </b-col>
          </b-form-group>

          <b-overlay :show="ntLoading" rounded="sm">
            <b-form v-if="ntSelectedCompanyId" @submit.prevent="saveNt">
              <h6 class="text-muted mb-3">Thông tin tài khoản</h6>
              <b-row>
                <b-col cols="4">
                  <b-form-group label="Partner ID">
                    <b-form-input v-model="ntForm.ntPartnerId" placeholder="Do Nhất Tín cấp" />
                  </b-form-group>
                </b-col>
                <b-col cols="4">
                  <b-form-group label="Tên đăng nhập">
                    <b-form-input
                      v-model="ntForm.username"
                      :placeholder="ntForm.hasCredentials ? '••••••• (để trống nếu không đổi)' : ''"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="4">
                  <b-form-group label="Mật khẩu">
                    <b-form-input
                      v-model="ntForm.password"
                      type="password"
                      :placeholder="ntForm.hasCredentials ? '••••••• (để trống nếu không đổi)' : ''"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <div class="d-flex justify-content-end">
                <b-button type="submit" variant="primary" :disabled="ntSaving">
                  <b-spinner v-if="ntSaving" small class="mr-1" />
                  {{ ntSaving ? 'Đang lưu...' : 'Kết nối & Lưu' }}
                </b-button>
              </div>
            </b-form>
            <div v-else class="text-center text-muted py-3" style="font-size:13px">
              Chọn doanh nghiệp để xem và chỉnh sửa cấu hình Nhất Tín
            </div>
          </b-overlay>

          <div v-if="ntConfig && ntConfig.ntAccessTokenExpiredAt" class="mt-3 p-2 bg-light rounded" style="font-size:12px">
            Access token hết hạn: <strong>{{ formatDate(ntConfig.ntAccessTokenExpiredAt) }}</strong>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';

const ShippingRepo = RepositoryFactory.get('shipping');

const defaultVtpForm = () => ({
  authMethod: 'secretToken',
  vtpSecretToken: '',
  username: '',
  password: '',
  hasCredentials: false,
});

const defaultNtForm = () => ({
  ntPartnerId: null,
  username: '',
  password: '',
  hasCredentials: false,
});

export default {
  name: 'ShippingCarrierSettings',
  data() {
    return {
      isLoading: false,
      // VTP
      vtpConfig: null,
      vtpForm: defaultVtpForm(),
      saving: null,
      testingCarrier: null,
      // NT
      companies: [],
      ntSelectedCompanyId: null,
      ntConfig: null,
      ntForm: defaultNtForm(),
      ntSaving: false,
      ntLoading: false,
    };
  },
  computed: {
    companyOptions() {
      return this.companies.map((c) => ({ value: c.id, text: c.name }));
    },
    hasVtpFormData() {
      const f = this.vtpForm;
      return !!(f.vtpSecretToken || f.username || f.hasCredentials);
    },
    hasNtFormData() {
      const f = this.ntForm;
      return !!(f.username || f.ntPartnerId || f.hasCredentials);
    },
  },
  mounted() {
    this.fetchAll();
  },
  methods: {
    async fetchAll() {
      this.isLoading = true;
      try {
        const [configs, companiesRes] = await Promise.all([
          ShippingRepo.getCarriers(),
          ApiService.query('v1/companies/all'),
        ]);
        this.companies = companiesRes.data.data || [];
        const vtp = configs.find((c) => c.carrier === 'viettelpost');
        if (vtp) {
          this.vtpConfig = { ...vtp, isActive: Boolean(vtp.isActive) };
          this.vtpForm.hasCredentials = !!(vtp.vtpTokenExpiredAt);
        }
      } catch {
        makeToastFaile('Không tải được cấu hình nhà vận chuyển');
      } finally {
        this.isLoading = false;
      }
    },

    async onNtCompanyChange(companyId) {
      if (!companyId) return;
      this.ntLoading = true;
      this.ntConfig = null;
      this.ntForm = defaultNtForm();
      try {
        const configs = await ShippingRepo.getCarriers(companyId);
        const nt = configs.find((c) => c.carrier === 'nhattin');
        if (nt) {
          this.ntConfig = { ...nt, isActive: Boolean(nt.isActive) };
          this.ntForm = {
            ...defaultNtForm(),
            ntPartnerId: nt.ntPartnerId || null,
            hasCredentials: !!(nt.ntAccessTokenExpiredAt),
          };
        }
      } catch {
        makeToastFaile('Không tải được cấu hình Nhất Tín');
      } finally {
        this.ntLoading = false;
      }
    },

    async saveVtp() {
      this.saving = 'viettelpost';
      try {
        const payload = { carrier: 'viettelpost' };
        if (this.vtpForm.authMethod === 'secretToken') {
          if (this.vtpForm.vtpSecretToken) payload.vtpSecretToken = this.vtpForm.vtpSecretToken;
        } else {
          if (this.vtpForm.username) payload.username = this.vtpForm.username;
          if (this.vtpForm.password) payload.password = this.vtpForm.password;
        }
        await ShippingRepo.upsertCarrier(payload);
        makeToastSuccess('Đã lưu cấu hình ViettelPost');
        await this.fetchAll();
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Lưu thất bại');
      } finally {
        this.saving = null;
      }
    },

    async saveNt() {
      if (!this.ntSelectedCompanyId) return;
      this.ntSaving = true;
      try {
        const payload = { carrier: 'nhattin', targetCompanyId: this.ntSelectedCompanyId };
        if (this.ntForm.ntPartnerId) payload.ntPartnerId = this.ntForm.ntPartnerId;
        if (this.ntForm.username) payload.username = this.ntForm.username;
        if (this.ntForm.password) payload.password = this.ntForm.password;
        await ShippingRepo.upsertCarrier(payload);
        makeToastSuccess('Đã lưu cấu hình Nhất Tín');
        await this.onNtCompanyChange(this.ntSelectedCompanyId);
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Lưu thất bại');
      } finally {
        this.ntSaving = false;
      }
    },

    async toggleActive(carrier, value, configId) {
      try {
        await ShippingRepo.updateCarrier(configId, { isActive: value });
        makeToastSuccess(value ? 'Đã bật nhà vận chuyển' : 'Đã tắt nhà vận chuyển');
      } catch {
        makeToastFaile('Cập nhật thất bại');
        if (carrier === 'viettelpost') await this.fetchAll();
        else await this.onNtCompanyChange(this.ntSelectedCompanyId);
      }
    },

    async testVtpConnection() {
      this.testingCarrier = 'viettelpost';
      try {
        const hasTyped = !!(this.vtpForm.vtpSecretToken || this.vtpForm.username || this.vtpForm.password);
        if (hasTyped) {
          const payload = { carrier: 'viettelpost' };
          if (this.vtpForm.authMethod === 'secretToken') {
            if (this.vtpForm.vtpSecretToken) payload.vtpSecretToken = this.vtpForm.vtpSecretToken;
          } else {
            if (this.vtpForm.username) payload.username = this.vtpForm.username;
            if (this.vtpForm.password) payload.password = this.vtpForm.password;
          }
          await ShippingRepo.testCarrierConnectionWithCredentials(payload);
        } else {
          if (!this.vtpConfig) throw new Error('Không tìm thấy cấu hình');
          await ShippingRepo.testCarrierConnection(this.vtpConfig.id);
        }
        makeToastSuccess('Kết nối thành công!');
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Kết nối thất bại');
      } finally {
        this.testingCarrier = null;
      }
    },

    async testNtConnection() {
      if (!this.ntSelectedCompanyId) return;
      this.testingCarrier = 'nhattin';
      try {
        const hasTyped = !!(this.ntForm.username || this.ntForm.password || this.ntForm.ntPartnerId);
        if (hasTyped) {
          const payload = { carrier: 'nhattin', targetCompanyId: this.ntSelectedCompanyId };
          if (this.ntForm.ntPartnerId) payload.ntPartnerId = this.ntForm.ntPartnerId;
          if (this.ntForm.username) payload.username = this.ntForm.username;
          if (this.ntForm.password) payload.password = this.ntForm.password;
          await ShippingRepo.testCarrierConnectionWithCredentials(payload);
        } else {
          if (!this.ntConfig) throw new Error('Không tìm thấy cấu hình');
          await ShippingRepo.testCarrierConnection(this.ntConfig.id);
        }
        makeToastSuccess('Kết nối thành công!');
      } catch (err) {
        makeToastFaile(err?.response?.data?.message || 'Kết nối thất bại');
      } finally {
        this.testingCarrier = null;
      }
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('vi-VN');
    },
  },
};
</script>
